import type { Archetype, DimensionId, QuizQuestion } from '../types/investmentPhilosophy';

export const ARCHETYPES: Archetype[] = [
  {
    id: 'buffett',
    name: 'Warren Buffett',
    descriptor: 'Quality + Patience',
    bio:
      "Buys wonderful businesses at fair prices and holds them for decades. Sticks to what he understands and lets compounding do the heavy lifting.",
    photoUrl: '/assets/investment-philosophy/buffett.jpg',
    vector: { microMacro: -2, valuationNarrative: -1, contrarianTrend: -1, concDiversified: -2, convictionCaution: 1 },
  },
  {
    id: 'lynch',
    name: 'Peter Lynch',
    descriptor: 'Growth + Common Sense',
    bio:
      "Famous for 'invest in what you know'. Looks for everyday companies with strong growth that the market hasn't priced in yet.",
    photoUrl: '/assets/investment-philosophy/lynch.jpg',
    vector: { microMacro: -2, valuationNarrative: 1, contrarianTrend: -1, concDiversified: 1, convictionCaution: 1 },
  },
  {
    id: 'wood',
    name: 'Cathie Wood',
    descriptor: 'Disruption + Conviction',
    bio:
      "Backs companies building the future of AI, genomics, and electric vehicles. Comfortable with volatility for the chance at outsized long-term returns.",
    photoUrl: '/assets/investment-philosophy/wood.jpg',
    vector: { microMacro: -1, valuationNarrative: 2, contrarianTrend: -1, concDiversified: -2, convictionCaution: 2 },
  },
  {
    id: 'dalio',
    name: 'Ray Dalio',
    descriptor: 'Macro + Systems',
    bio:
      "Studies how the economic machine works and builds rules-based, all-weather portfolios that aim to perform across every market regime.",
    photoUrl: '/assets/investment-philosophy/dalio.jpg',
    vector: { microMacro: 2, valuationNarrative: 0, contrarianTrend: 0, concDiversified: 2, convictionCaution: -1 },
  },
  {
    id: 'marks',
    name: 'Howard Marks',
    descriptor: 'Risk + Cycles',
    bio:
      "Obsessed with risk and market cycles. Believes the price you pay matters more than the asset you own, and that patience pays.",
    photoUrl: '/assets/investment-philosophy/marks.jpg',
    vector: { microMacro: 1, valuationNarrative: -2, contrarianTrend: -2, concDiversified: 0, convictionCaution: -2 },
  },
  {
    id: 'bogle',
    name: 'John Bogle',
    descriptor: 'Simplicity + Discipline',
    bio:
      "Founder of Vanguard and the patron saint of index investing. Owns the whole market, keeps costs low, and holds forever.",
    photoUrl: '/assets/investment-philosophy/bogle.jpg',
    vector: { microMacro: 0, valuationNarrative: 0, contrarianTrend: 0, concDiversified: 2, convictionCaution: -1 },
  },
  {
    id: 'paulTudorJones',
    name: 'Paul Tudor Jones',
    descriptor: 'Momentum + Risk Management',
    bio:
      "Macro trader famous for catching big trends and cutting losers fast. Rides momentum hard but is ruthlessly disciplined when a trade goes against him.",
    photoUrl: '/assets/investment-philosophy/paulTudorJones.jpg',
    vector: { microMacro: 1, valuationNarrative: 1, contrarianTrend: 2, concDiversified: 1, convictionCaution: 0 },
  },
  {
    id: 'burry',
    name: 'Michael Burry',
    descriptor: 'Deep Value + Contrarian',
    bio:
      "Hunts for broken or overlooked situations everyone else has given up on. Bets big on his research and waits, sometimes years, to be proven right.",
    photoUrl: '/assets/investment-philosophy/burry.jpg',
    vector: { microMacro: -1, valuationNarrative: -2, contrarianTrend: -2, concDiversified: -2, convictionCaution: 2 },
  },
  {
    id: 'soros',
    name: 'George Soros',
    descriptor: 'Macro + Reflexivity',
    bio:
      "Plays big macro themes like currencies, interest rates, and political shocks. When the setup is right, he sizes up aggressively and acts fast.",
    photoUrl: '/assets/investment-philosophy/soros.jpg',
    vector: { microMacro: 2, valuationNarrative: 1, contrarianTrend: 2, concDiversified: -1, convictionCaution: 2 },
  },
];

export const DIMENSION_LABELS: Record<DimensionId, { negative: string; positive: string }> = {
  microMacro: { negative: 'Micro', positive: 'Macro' },
  valuationNarrative: { negative: 'Valuation', positive: 'Narrative' },
  contrarianTrend: { negative: 'Contrarian', positive: 'Trend-Following' },
  concDiversified: { negative: 'Concentrated', positive: 'Diversified' },
  convictionCaution: { negative: 'Caution', positive: 'Conviction' },
};

export const DIMENSION_ORDER: DimensionId[] = [
  'microMacro',
  'valuationNarrative',
  'contrarianTrend',
  'concDiversified',
  'convictionCaution',
];

export const QUESTIONS: QuizQuestion[] = [
  // Dimension 1: Micro vs Macro
  {
    id: 'd1q1',
    text: 'I find it interesting to think about how politics, the economy, and global trends connect.',
    impacts: [{ dimension: 'microMacro', polarity: 1 }],
  },
  {
    id: 'd1q2',
    text: 'When the news gets noisy, I tune it out and focus on my own life.',
    impacts: [{ dimension: 'microMacro', polarity: -1 }],
  },
  {
    id: 'd1q3',
    text: "Before making a big life decision, I think about what's happening in the wider world.",
    impacts: [{ dimension: 'microMacro', polarity: 1 }],
  },

  // Dimension 2: Valuation vs Narrative
  {
    id: 'd2q1',
    text: "There's no need to upgrade my phone if my existing one is working just fine.",
    impacts: [{ dimension: 'valuationNarrative', polarity: -1 }],
  },
  {
    id: 'd2q2',
    text: "I'm happy to pay more if it means getting the newest or best version of something.",
    impacts: [{ dimension: 'valuationNarrative', polarity: 1 }],
  },
  {
    id: 'd2q3',
    text: "I'll wait months for a price drop before buying something I want.",
    impacts: [{ dimension: 'valuationNarrative', polarity: -1 }],
  },

  // Dimension 3: Contrarian vs Trend-Following
  {
    id: 'd3q1',
    text: 'When something becomes trendy, I lose interest in it.',
    impacts: [{ dimension: 'contrarianTrend', polarity: -1 }],
  },
  {
    id: 'd3q2',
    text: "Once I see something clearly working, I want in — even if I'm not the first.",
    impacts: [{ dimension: 'contrarianTrend', polarity: 1 }],
  },
  {
    id: 'd3q3',
    text: "If a restaurant has a long line out the door, that's a strong signal it's worth the wait.",
    impacts: [{ dimension: 'contrarianTrend', polarity: 1 }],
  },

  // Dimension 4: Concentrated vs Diversified
  {
    id: 'd4q1',
    text: 'When I get into a hobby, I go deep on it rather than dabbling in lots of things.',
    impacts: [{ dimension: 'concDiversified', polarity: -1 }],
  },
  {
    id: 'd4q2',
    text: "I'd rather try a little bit of many things than master one thing.",
    impacts: [{ dimension: 'concDiversified', polarity: 1 }],
  },
  {
    id: 'd4q3',
    text: "I'm comfortable putting most of my time and energy into one big project, person, or goal.",
    impacts: [{ dimension: 'concDiversified', polarity: -1 }],
  },

  // Dimension 5: Caution vs Conviction
  {
    id: 'd5q1',
    text: "If I really believe in something, I'll stick with it even when it stops working for a while.",
    impacts: [{ dimension: 'convictionCaution', polarity: 1 }],
  },
  {
    id: 'd5q2',
    text: 'When something starts going wrong, my instinct is to pull back fast and reassess.',
    impacts: [{ dimension: 'convictionCaution', polarity: -1 }],
  },
  {
    id: 'd5q3',
    text: 'Big surprises stress me out more than they excite me.',
    impacts: [{ dimension: 'convictionCaution', polarity: -1 }],
  },

  // Flavor / Temperament Questions (multi-dimensional)
  {
    id: 'flavor1',
    text: "If I don't really understand something, I'd rather sit it out than guess my way through.",
    impacts: [
      { dimension: 'microMacro', polarity: -1, weight: 1 / 2 },
      { dimension: 'convictionCaution', polarity: -1, weight: 1 / 2 },
    ],
  },
  {
    id: 'flavor2',
    text: "When I'm convinced about something most people can't see yet, I'm willing to commit big and wait years to be proven right.",
    impacts: [
      { dimension: 'contrarianTrend', polarity: -1, weight: 1 / 3 },
      { dimension: 'concDiversified', polarity: -1, weight: 1 / 3 },
      { dimension: 'convictionCaution', polarity: 1, weight: 1 / 3 },
    ],
  },
  {
    id: 'flavor3',
    text: 'The simple, boring option usually beats the clever one in the long run.',
    impacts: [
      { dimension: 'concDiversified', polarity: 1, weight: 1 / 2 },
      { dimension: 'convictionCaution', polarity: -1, weight: 1 / 2 },
    ],
  },
];
